import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Paper,
  Typography,
} from '@mui/material';
import { AppProvider } from '@toolpad/core/AppProvider';

import { useAuth } from '../../context/AuthContext';
import { accountService } from '../../services/api/accountService';
import { businessService } from '../../services/api/businessService';
import { appTheme } from '../../theme/appTheme';
import DashboardNavigationBar from '../DashboardNavigationBar/DashboardNavigationBar';
import StorefrontOfflineAlert from '../StorefrontOfflineAlert/StorefrontOfflineAlert';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import { LegalDocumentTitle } from '../LegalPage/LegalPage.styled';
import {
  DashboardContainer,
  SidebarContainer,
  ContentWrapper,
} from '../BusinessDashboard/BusinessDashboard.styled';
import { PartnershipSection } from './PartnershipAgreementDashboard.styled';

const PDF_FILE = 'Business Partnership Agreement.pdf';

function buildPdfSrc() {
  const base = process.env.PUBLIC_URL || '';
  const path = `${base}/legal/${encodeURIComponent(PDF_FILE).replace(/%2F/g, '/')}`;
  return `${path}#navpanes=0&zoom=100`;
}

function formatAcceptedAt(iso) {
  if (!iso || typeof iso !== 'string') return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function PartnershipAgreementDashboard({ window: windowProp }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const pdfSrc = useMemo(() => buildPdfSrc(), []);
  const acceptedAt = businessData?.PartnershipAgreementAcceptedAt;
  const isSigned = Boolean(acceptedAt);
  const signerLabel = businessData?.PartnershipAgreementAcceptedBy || null;

  useEffect(() => {
    const url = pdfSrc.split('#')[0];
    fetch(url, { method: 'HEAD' })
      .then((res) => {
        if (res.ok) setPdfReady(true);
      })
      .catch(() => {});
  }, [pdfSrc]);

  useEffect(() => {
    if (!user) return;
    if (user?.Role !== 'business_user' && user?.Role !== 'admin_user') {
      navigate('/');
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const userResponse = await accountService.getAccount();
        if (cancelled) return;
        setUserData(userResponse);

        if (!userResponse.BusinessId) {
          setLoading(false);
          return;
        }

        const biz = await businessService.getBusinessForOwnerDashboard(userResponse.BusinessId);
        if (!cancelled) {
          setBusinessData(biz);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  const handleSubmitAcceptance = async () => {
    if (!businessData?.BusinessId || !acknowledged || isSigned) return;
    setSubmitting(true);
    try {
      const updated = await businessService.editBusiness(businessData.BusinessId, {
        PartnershipAgreementAccepted: true,
      });
      setBusinessData(updated);
      setToast({
        open: true,
        severity: 'success',
        message: 'Partnership agreement acceptance recorded.',
      });
      setAcknowledged(false);
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Could not save. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const demoWindow = windowProp ? windowProp() : undefined;

  if (!user || loading) {
    return (
      <AppProvider branding={{ title: 'NKY Connect' }} theme={appTheme} window={demoWindow}>
        <DashboardContainer>
          <SidebarContainer>
            <DashboardNavigationBar />
          </SidebarContainer>
          <ContentWrapper
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
          >
            <NkySpinner />
          </ContentWrapper>
        </DashboardContainer>
      </AppProvider>
    );
  }

  if (!userData?.BusinessId) {
    return (
      <AppProvider branding={{ title: 'NKY Connect' }} theme={appTheme} window={demoWindow}>
        <DashboardContainer>
          <SidebarContainer>
            <DashboardNavigationBar />
          </SidebarContainer>
          <ContentWrapper>
            <Alert severity="info" sx={{ borderRadius: '12px', maxWidth: 560 }}>
              Partnership Agreement is available to business accounts linked to a business.
            </Alert>
          </ContentWrapper>
        </DashboardContainer>
      </AppProvider>
    );
  }

  return (
    <AppProvider
      branding={{ title: businessData?.BusinessName || 'Partnership Agreement' }}
      theme={appTheme}
      window={demoWindow}
    >
      <DashboardContainer>
        <SidebarContainer>
          <DashboardNavigationBar />
        </SidebarContainer>

        <ContentWrapper>
          {!businessData?.BusinessPublished && <StorefrontOfflineAlert />}

          {!isSigned && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
              Accept this agreement so NKY Connect can publish your storefront after review.
            </Alert>
          )}

          <PartnershipSection>
            <LegalDocumentTitle>Business Partnership Agreement</LegalDocumentTitle>
            <Typography variant="body2" sx={{ color: '#5a6478', mb: 2, maxWidth: 720 }}>
              Read the agreement below, then confirm acceptance at the bottom. This applies to your
              business on NKY Connect.
            </Typography>

            {isSigned && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
                Agreement accepted on <strong>{formatAcceptedAt(acceptedAt)}</strong>
                {signerLabel ? (
                  <>
                    {' '}
                    by <strong>{signerLabel}</strong>
                  </>
                ) : null}
                .
              </Alert>
            )}

            {!pdfReady ? (
              <Typography variant="body2" color="text.secondary">
                Loading document…
              </Typography>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <Box
                  component="iframe"
                  src={pdfSrc}
                  title="Business Partnership Agreement"
                  sx={{
                    width: '100%',
                    height: '88vh',
                    border: 'none',
                    display: 'block',
                    backgroundColor: '#fff',
                  }}
                />
              </Paper>
            )}

            <Typography variant="body2" sx={{ mb: 2 }}>
              If the PDF does not load,{' '}
              <a href={pdfSrc.split('#')[0]} target="_blank" rel="noopener noreferrer">
                open it in a new tab
              </a>
              .
            </Typography>

            {!isSigned && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  border: '1px solid rgba(13, 78, 254, 0.12)',
                  background:
                    'linear-gradient(135deg, rgba(1, 235, 178, 0.08) 0%, rgba(13, 78, 254, 0.06) 100%)',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#1a1a2e', fontWeight: 600 }}>
                      I have read and agree to the NKY Connect Business Partnership Agreement on
                      behalf of my business.
                    </Typography>
                  }
                />
                <FormHelperText sx={{ ml: 0, mr: 0, mb: 2, color: '#5a6478' }}>
                  Checking this box records your acceptance and notifies our systems that this
                  business may be published after review.
                </FormHelperText>
                <Button
                  variant="contained"
                  disabled={!acknowledged || submitting}
                  onClick={handleSubmitAcceptance}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '10px',
                    px: 3,
                    background: 'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
                    boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
                  }}
                >
                  {submitting ? 'Saving…' : 'Submit acceptance'}
                </Button>
              </Paper>
            )}
          </PartnershipSection>
        </ContentWrapper>

        <NkySnackbarAlert
          open={toast.open}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          message={toast.message}
        />
      </DashboardContainer>
    </AppProvider>
  );
}

PartnershipAgreementDashboard.propTypes = {
  window: PropTypes.func,
};

export default PartnershipAgreementDashboard;
